import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { CriticalMarkdown } from "@/components/site/CriticalMarkdown";
import { getPublicSpanishBlogPost } from "@/lib/blog.functions";
import { breadcrumbJsonLd } from "@/lib/seo";
import { buildCorporateHead } from "@/lib/corporate-seo";
import { SITE_URL } from "@/lib/i18n";
import { setEdgeHtmlCache, markNotFoundResponse } from "@/lib/cache-headers";

const LEGACY: Record<string, string> = {
  "shopify-migration": "shopify-migration-timeline",
  "5-custom-shopify-solutions": "custom-shopify-solutions-for-established-brands",
  "shopify-store-setup-experts": "shopify-store-setup-playbook",
  "ecommerce-seo-agency": "shopify-technical-seo-playbook",
  "how-to-add-a-size-guide-on-shopify": "shopify-size-guide-implementation",
  "create-a-personalized-product-recommender": "shopify-liquid-product-recommender",
  "shopify-software-development": "shopify-development-guide",
  "common-shopify-issues-and-solutions": "common-shopify-issues-fixes",
};

// Spanish articles consolidated into a stronger sibling. They answer with a
// permanent redirect to the article that now owns the topic.
const MERGED: Record<string, string> = {
  "fallas-mapas-redirecciones-migraciones-shopify": "cadenas-redirecciones-shopify-diagnostico",
  "automatizacion-segura-theme-shopify": "duplicar-theme-shopify-antes-de-cambios",
};

const CATEGORY_LABEL: Record<string, string> = {
  performance: "Rendimiento",
  migrations: "Migraciones",
  seo: "SEO técnico",
  "shopify-engineering": "Ingeniería Shopify",
  shopify: "Integraciones",
};

/**
 * Builds FAQPage data from a "## Preguntas frecuentes" section whose
 * questions are H3 headings. Only text visible on the page is marked up.
 */
function faqFromMarkdown(markdown: string) {
  const match = markdown.match(/^##\s+Preguntas frecuentes\s*$([\s\S]*?)(?=^##\s|(?![\s\S]))/m);
  if (!match) return null;
  const items = [...match[1].matchAll(/^###\s+(.+?)\s*$([\s\S]*?)(?=^###\s|(?![\s\S]))/gm)]
    .map(([, q, a]) => ({
      question: q.trim(),
      answer: a
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/[*_`]/g, "")
        .replace(/\s+/g, " ")
        .trim(),
    }))
    .filter((i) => i.question && i.answer);
  if (items.length < 2) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
      "@type": "Question",
      name: i.question,
      acceptedAnswer: { "@type": "Answer", text: i.answer },
    })),
  };
}

// reading_minutes is shared with the English row, so derive the Spanish
// figure from the Spanish body instead.
function readingMinutes(markdown: string) {
  return Math.max(1, Math.round(markdown.split(/\s+/).filter(Boolean).length / 200));
}

const HERO_SIZES = "(min-width: 768px) 720px, calc(100vw - 48px)";

/**
 * Spanish covers ship as /assets/blog/es/<slug>.jpg with -800, .webp and -800.webp
 * siblings (1600x900 and 800x450). Returns null for any other path so
 * legacy images still render through the plain <img> fallback.
 */
function coverSources(src: string | null | undefined) {
  const m = src?.match(/^\/assets\/blog\/es\/([a-z0-9-]+)\.jpg$/);
  if (!m) return null;
  const base = `/assets/blog/es/${m[1]}`;
  return {
    jpg: `${base}.jpg`,
    jpgSrcset: `${base}-800.jpg 800w, ${base}.jpg 1600w`,
    webp1600: `${base}.webp`,
    webpSrcset: `${base}-800.webp 800w, ${base}.webp 1600w`,
  };
}

function absolute(url: string | null | undefined) {
  if (!url) return undefined;
  return url.startsWith("http") ? url : `${SITE_URL}${url}`;
}

export const Route = createFileRoute("/es/blog/$")({
  loader: async ({ params }) => {
    const slug = (params._splat ?? "").replace(/^\/+|\/+$/g, "");
    if (!slug) return { post: null, related: [] };
    if (LEGACY[slug]) throw redirect({ href: `/es/recursos/${LEGACY[slug]}`, statusCode: 301 });
    if (MERGED[slug]) throw redirect({ href: `/es/blog/${MERGED[slug]}`, statusCode: 301 });
    if (!/^[a-z0-9-]+$/.test(slug)) throw notFound();
    await setEdgeHtmlCache();
    const result = await getPublicSpanishBlogPost({ data: { slug } });
    if (!result.post) throw redirect({ href: "/es/recursos", statusCode: 301 });
    return result;
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) return {};
    const path = `/es/blog/${post.slug_es}`;
    // English sibling lives on the same row; pair them for hreflang.
    const enPath = post.slug ? `/blog/${post.slug}` : path;
    const description = post.meta_description_es || post.excerpt_es || post.title_es;
    const image = absolute(
      post.og_image_es || post.featured_image_es || post.og_image || post.featured_image,
    );
    const hero = coverSources(post.featured_image_es);
    const schemaImages = [absolute(post.featured_image_es), image].filter(
      (u, i, all): u is string => Boolean(u) && all.indexOf(u) === i,
    );
    const section = CATEGORY_LABEL[post.category] ?? "Shopify";
    const faq = faqFromMarkdown(post.content_es);
    const head = buildCorporateHead({
      locale: "es",
      title: `${post.meta_title_es || post.title_es} · Shugert`,
      description,
      path,
      altPath: enPath,
      noindex: post.noindex,
      type: "article",
      publishedTime: post.published_at,
      modifiedTime: post.updated_at,
      author: post.author,
      articleAuthor: post.author,
      articleSection: section,
      ...(image ? { ogImage: image } : {}),
      // The cover is the LCP element on mobile and desktop; let the browser
      // fetch it from the head instead of discovering it during layout.
      ...(hero
        ? {
            preloadImage: hero.webp1600,
            preloadImageType: "image/webp",
            preloadImageSrcset: hero.webpSrcset,
            preloadImageSizes: HERO_SIZES,
          }
        : {}),
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title_es,
          description,
          datePublished: post.published_at,
          dateModified: post.updated_at,
          inLanguage: "es-MX",
          mainEntityOfPage: `${SITE_URL}${path}`,
          ...(schemaImages.length ? { image: schemaImages } : {}),
          articleSection: section,
          timeRequired: `PT${readingMinutes(post.content_es)}M`,
          ...(post.slug
            ? {
                translationOfWork: {
                  "@type": "BlogPosting",
                  "@id": `${SITE_URL}${enPath}`,
                  url: `${SITE_URL}${enPath}`,
                  inLanguage: "en-US",
                },
              }
            : {}),
          author:
            post.author === "Samuel Noriega"
              ? {
                  "@type": "Person",
                  "@id": `${SITE_URL}/about/samuel-noriega#person`,
                  name: post.author,
                  url: `${SITE_URL}/about/samuel-noriega`,
                  jobTitle: "Fundador y CEO, Shugert",
                }
              : { "@type": "Person", name: post.author },
          publisher: { "@type": "Organization", name: "Shugert", "@id": `${SITE_URL}#org` },
        },
        breadcrumbJsonLd([
          { name: "Inicio", path: "/es" },
          { name: "Blog", path: "/es/blog" },
          { name: post.title_es, path },
        ]),
        ...(faq ? [faq] : []),
      ],
    });
    return head;
  },
  notFoundComponent: () => {
    markNotFoundResponse();
    return (
      <SiteShell>
        <main className="mx-auto max-w-3xl px-6 py-24">
          <h1 className="text-3xl font-semibold">Artículo no encontrado</h1>
          <a className="mt-6 inline-block underline" href="/es/blog">
            Ver el blog
          </a>
        </main>
      </SiteShell>
    );
  },
  component: SpanishArticle,
});

// Each topic cluster sends readers to the service page that owns it, with a
// call to action that names the concrete next step instead of a generic one.
const SERVICE: Record<string, { href: string; label: string; cta: string; pitch: string }> = {
  performance: {
    href: "/es/rendimiento",
    label: "Optimización de rendimiento Shopify",
    cta: "Solicitar diagnóstico de rendimiento",
    pitch:
      "Medimos tu tienda con datos de campo, aislamos la causa de LCP, INP o CLS y entregamos correcciones probadas en un tema de prueba.",
  },
  migrations: {
    href: "/es/migracion-shopify",
    label: "Migración a Shopify",
    cta: "Cotizar mi migración",
    pitch:
      "Inventario de URLs, modelo de catálogo, mapa de redirecciones y monitoreo posterior al lanzamiento, con responsables y criterios de aceptación.",
  },
  seo: {
    href: "/es/seo-tecnico-shopify",
    label: "SEO técnico para Shopify",
    cta: "Solicitar auditoría de SEO técnico",
    pitch:
      "Revisamos canonicals, enlazado, datos estructurados e indexación sobre una muestra real de tu catálogo.",
  },
  "shopify-engineering": {
    href: "/es/desarrollo-shopify",
    label: "Desarrollo Shopify",
    cta: "Hablar con un desarrollador senior",
    pitch:
      "Desarrollo de temas y apps con control de versiones, ambientes de prueba y un plan de regreso para cada publicación.",
  },
  shopify: {
    href: "/es/integraciones-shopify",
    label: "Integraciones Shopify",
    cta: "Revisar mi integración",
    pitch:
      "Diseñamos integraciones con ERP, WMS y 3PL que se recuperan de fallas, no duplican pedidos y se concilian solas.",
  },
};

// Keyed by Spanish slug: the article's topic decides the service it feeds,
// which is not always the category shared with the English row.
const SERVICE_OVERRIDE: Record<string, keyof typeof SERVICE | "plus"> = {
  "cadenas-redirecciones-shopify-diagnostico": "migrations",
  "sincronizacion-inventario-shopify-erp-wms-3pl": "shopify",
  "integracion-shopify-erp-webhooks-reintentos-conciliacion": "shopify",
  "auditoria-deuda-tecnica-shopify-plus": "plus",
};

const PLUS = {
  href: "/es/agencia-shopify-plus",
  label: "Agencia Shopify Plus",
  cta: "Auditar mi tienda Shopify Plus",
  pitch:
    "Inventario de Scripts, apps e integraciones, priorizado por riesgo y convertido en un backlog ejecutable.",
};

function SpanishArticle() {
  const { post, related } = Route.useLoaderData();
  if (!post) return null;
  const override = SERVICE_OVERRIDE[post.slug_es];
  const service = override === "plus" ? PLUS : SERVICE[override ?? post.category];
  const fmt = new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "America/Mexico_City",
  });
  const updated =
    post.updated_at &&
    new Date(post.updated_at).getTime() - new Date(post.published_at).getTime() > 86_400_000;
  return (
    <SiteShell>
      <article className="mx-auto max-w-3xl px-6 py-16">
        <nav className="text-sm" aria-label="Ruta de navegación">
          <a href="/es/blog" className="underline">
            Blog
          </a>
          {CATEGORY_LABEL[post.category] && (
            <span className="text-muted-foreground"> / {CATEGORY_LABEL[post.category]}</span>
          )}
        </nav>
        <h1 className="mt-6 text-4xl font-semibold sm:text-5xl">{post.title_es}</h1>
        {post.excerpt_es && <p className="mt-5 text-lg text-muted-foreground">{post.excerpt_es}</p>}
        <p className="mt-5 text-sm text-muted-foreground">
          <a className="underline" href="/about/samuel-noriega" rel="author">
            {post.author}
          </a>
          , Fundador y CEO de Shugert ·{" "}
          <time dateTime={post.published_at}>{fmt.format(new Date(post.published_at))}</time>
          {updated && (
            <>
              {" "}
              · Actualizado{" "}
              <time dateTime={post.updated_at}>{fmt.format(new Date(post.updated_at))}</time>
            </>
          )}
          · {readingMinutes(post.content_es)} min de lectura
        </p>
        {post.featured_image_es && (
          <figure className="mt-10">
            {(() => {
              const hero = coverSources(post.featured_image_es);
              const img = (
                <img
                  src={hero?.jpg ?? post.featured_image_es}
                  srcSet={hero?.jpgSrcset}
                  sizes={hero ? HERO_SIZES : undefined}
                  alt={post.featured_image_alt_es || post.title_es}
                  width={1600}
                  height={900}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="h-auto w-full rounded-2xl border"
                />
              );
              return hero ? (
                <picture>
                  <source type="image/webp" srcSet={hero.webpSrcset} sizes={HERO_SIZES} />
                  {img}
                </picture>
              ) : (
                img
              );
            })()}
          </figure>
        )}
        <div className="prose prose-lg prose-neutral mt-12 max-w-none dark:prose-invert">
          <CriticalMarkdown
            components={{
              h1: ({ children, ...props }: any) => <h2 {...props}>{children}</h2>,
              img: () => null,
            }}
          >
            {post.content_es}
          </CriticalMarkdown>
        </div>
        {service && (
          <aside className="mt-12 rounded-2xl border p-6">
            <p className="text-sm uppercase tracking-wide text-muted-foreground">
              Servicio relacionado
            </p>
            <p className="mt-2 text-xl font-semibold">
              <a className="underline" href={service.href}>
                {service.label}
              </a>
            </p>
            <p className="mt-3 text-muted-foreground">{service.pitch}</p>
            <a
              href="/es/cotizacion"
              className="mt-5 inline-block rounded-full bg-foreground px-6 py-3 text-background"
            >
              {service.cta}
            </a>
          </aside>
        )}
        {related.length > 0 && (
          <section className="mt-12" aria-labelledby="relacionados">
            <h2 id="relacionados" className="text-2xl font-semibold">
              Sigue leyendo
            </h2>
            <ul className="mt-4 space-y-4">
              {related.map((r: any) => (
                <li key={r.slug_es}>
                  <a className="font-medium underline" href={`/es/blog/${r.slug_es}`}>
                    {r.title_es}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </SiteShell>
  );
}
